import React from "react";
import { DotLegalDeleteDialog, DotLegalMultiSelect } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation.ts";
import { useResponsibleQuery } from "../../user/hooks/useResponsibleQuery.ts";
import { AccessControlEntity, useAccessResponsibilityHelper } from "./useAccessResponsibilityHelper.hooks.ts";
import { Trans } from "react-i18next";

export interface AdditionalUserAccessFieldProps<T extends AccessControlEntity> {
    entity: T | undefined;
    isLoading: boolean;
    onChange: (changeFunc: (entity: T) => void) => Promise<void>;
    onConfirmNavigate: () => void;
    readOnly?: boolean;
    disabled: boolean;
    isPreview: boolean;
    errorText?: string;
    toolTipText?: string;
    warningText?: string;
    disableAccessWarning?: boolean;
}

function AdditionalUserAccessField<T extends AccessControlEntity>(props: AdditionalUserAccessFieldProps<T>) {
    const { translateString } = useTranslation();
    const { onFieldChange, handleDialogConfirm, pendingUserChanges, setPendingUserChanges } = useAccessResponsibilityHelper<T>(
        props.onChange,
        props.onConfirmNavigate,
        props.disableAccessWarning
    );

    const userQuery = useResponsibleQuery(!props.isPreview);

    return (
        <>
            <DotLegalMultiSelect
                options={(userQuery.dataByList(props.entity?.additionalAccessUsers ?? []) ?? [])
                    .filter((x) => !props.entity?.excludedUsers.includes(x.id))
                    .map((x) => ({ ...x, name: `${x.name}${x.active ? "" : ` (${translateString("deactivated")})`}` }))}
                isLoading={(userQuery.isLoading || props.isLoading) && !props.readOnly}
                onChange={
                    props.readOnly
                        ? () => {}
                        : (values) => {
                              let newEntity = { ...props.entity!, additionalAccessUsers: values };
                              onFieldChange(newEntity, props.entity!);
                          }
                }
                label={translateString("additionalUserAccess")}
                noOptionsLabel={translateString("noOptions")}
                warningText={props.isPreview || props.disableAccessWarning ? undefined : props.warningText}
                toolTipText={props.toolTipText}
                chosenOptions={props.entity?.additionalAccessUsers ?? []}
                disabled={props.disabled}
                errorText={props.errorText}
            />

            {pendingUserChanges && (
                <DotLegalDeleteDialog
                    open
                    headerText={translateString("confirmAccessRemoval")}
                    alertBodyText={
                        <Trans
                            i18nKey="confirmSystemAccessRemovalBody"
                            components={{
                                ul: <ul />,
                                li: <li />,
                            }}
                        />
                    }
                    warningTranslation={translateString("warning")}
                    deleteBtnText={translateString("save")}
                    cancelBtnText={translateString("cancel")}
                    onDialogClose={() => setPendingUserChanges(null)}
                    onOkClick={() => handleDialogConfirm()}
                />
            )}
        </>
    );
}

export default AdditionalUserAccessField;
